import { computed, ref } from "vue";
import { defineStore } from "pinia";
import { Mutex } from "async-mutex";
import { readCookie } from "@/utils/cookies/JsonCookieReader";
import { writeCookie } from "@/utils/cookies/JsonCookieWriter";
import { useCurrentUserStore } from "./CurrentUserStore";
import { ReminderFilters } from "@/types/reminders/ReminderFilters";

export const useRemindersStore = defineStore("remindersStore", () => {
  const mutex = new Mutex();
  const cachedReminderGroup = ref<number | undefined>();
  const cachedFilters = ref<ReminderFilters>();
  const currentUserStore = useCurrentUserStore();
  const remindersRefreshingField = ref(false);

  const remindersRefreshing = computed({
    get: () => remindersRefreshingField.value,
    set: (value: boolean) => (remindersRefreshingField.value = value),
  });

  /** Constructs cookie name for currently logged in user and site prefix */
  const getCookieName = async (prefix: string) => {
    const currentUser = await currentUserStore.getCurrentUserDetails();
    const sitePrefix = await currentUserStore.getSitePrefix();
    return [prefix, sitePrefix, currentUser.id]
      .filter((item) => !!item)
      .join("-");
  };

  /** Gets the previous Reminder filters */
  const getFilters = async () => {
    const filters = await mutex.runExclusive(async () => {
      // To improve performance, only read the cookie we don't already have the data
      if (!cachedFilters.value) {
        cachedFilters.value = readCookie<ReminderFilters>(
          await getCookieName("x-reminder-filters"),
        );
      }

      return cachedFilters.value;
    });

    return filters;
  };

  /** Stores the current Reminder filters so filters are remembered
   * when user navigates back to the Reminders page */
  const updateFilters = async (filters: ReminderFilters) => {
    await mutex.runExclusive(async () => {
      /* Improve performance by caching the updated filters
       * This means the app won't need to read the cookie unnecessarily */
      cachedFilters.value = filters;

      // Update cookie so filters are remembered when user reloads the app
      writeCookie<ReminderFilters>(
        await getCookieName("x-reminder-filters"),
        filters,
      );
    });
  };

  /** Gets the previous Reminder filters */
  const getSelectedReminderGroup = async () => {
    const group = await mutex.runExclusive(async () => {
      // To improve performance, only read the cookie we don't already have the data
      if (cachedReminderGroup.value == undefined) {
        cachedReminderGroup.value = readCookie<number>(
          await getCookieName("x-reminder-selected-group"),
        );
      }

      return cachedReminderGroup.value;
    });

    return group;
  };

  /** Stores the current Reminder group so selected tab is remembered
   * when user navigates back to the Reminders page */
  const updateSelectedReminderGroup = async (group: number) => {
    await mutex.runExclusive(async () => {
      cachedReminderGroup.value = group;

      // Update cookie so selected group is remembered when user reloads the app
      writeCookie<number>(
        await getCookieName("x-reminder-selected-group"),
        group,
      );
    });
  };

  /** Clears the current filters cache */
  const clearCache = () => {
    cachedFilters.value = undefined;
    cachedReminderGroup.value = undefined;
  };

  return {
    getFilters,
    updateFilters,
    getSelectedReminderGroup,
    updateSelectedReminderGroup,
    clearCache,
    remindersRefreshing,
  };
});
